import type { AnySceneObjectT, GroupStyleI, SelectInputT, SimpleStyleT } from "tweenly"
import Box from "@mui/material/Box"
import { useState } from "react"
import { transforms } from "../../../../data/styles/groups/transform.styles"
import { useObjectGroupActions } from "../../../../hooks/useObjectGroupActions"
import AddButton from "../../../common/AddButton"
import ExpandableSection from "../../../common/ExpandableSection"
import GroupFields from "../../../common/PropertyTools/GroupFields/GroupFields"
import CompactSelect from "../../../common/PropertyTools/CompactSelect"
import PropertyRow from "../../../common/PropertyTools/PropertyRow"
interface Props {
    object: AnySceneObjectT
    handleSetStyleValue: (property: any, value: any) => void
    handleSetObjectValue: (property: any, value: any) => void
}

const Transforms = ({ object, handleSetStyleValue, handleSetObjectValue }: Props) => {
    const { addObjectGroup, updateObjectGroups, deleteObjectGroup } = useObjectGroupActions()
    // bumped on every add: a collapsed section opens, so what was added is in view
    const [expandRequest, setExpandRequest] = useState(0)

    const handleAddGroup = (e: any) => {
        if (!e) e = window.event
        e.cancelBubble = true
        if (e.stopPropagation) e.stopPropagation()
        const updatedGroups = addObjectGroup("transforms", object["transforms"])
        handleSetObjectValue("transforms", updatedGroups)
        setExpandRequest((request) => request + 1)
    }

    const handleSetGroup = (index: number, property: any, value: any) => {
        const updatedGroups = updateObjectGroups(object["transforms"], index, property, value)
        handleSetObjectValue("transforms", updatedGroups)
    }

    const handleDeleteGroup = (index: number) => {
        const updatedGroups = deleteObjectGroup(object["transforms"], index)
        handleSetObjectValue("transforms", updatedGroups)
    }

    const transformOrigin: SimpleStyleT | undefined = object.styles.find(
        (style) => style.property === "transformOrigin"
    )
    const objectTransforms: GroupStyleI[] | undefined = object.transforms

    const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) =>
        handleSetStyleValue(e.target.name, e.target.value)

    if (!transformOrigin && !objectTransforms && !transforms) return <></>

    return (
        <ExpandableSection
            expanded={false}
            title={"Transforms"}
            expandRequest={expandRequest}
            secondaryActionComponent={
                <AddButton text="transform" handleAddObject={handleAddGroup} />
            }
            component={
                <Box display="flex" flexDirection="column" gap={1.5} width="100%" pb={0.5}>
                    {transformOrigin && (
                        <PropertyRow>
                            <Box display="flex" gap={1}>
                                <Box flex={1} minWidth={0}>
                                    <CompactSelect
                                        name={transformOrigin.property}
                                        value={transformOrigin.value}
                                        options={(transformOrigin.input as SelectInputT).props.map(
                                            (option: string) => ({
                                                value: option,
                                                label: option,
                                            })
                                        )}
                                        label={transformOrigin.input.label ?? "transform origin"}
                                        tooltip="Transform origin"
                                        handleOnChange={handleOnChange}
                                    />
                                </Box>
                                <Box flex={1} />
                            </Box>
                        </PropertyRow>
                    )}
                    {objectTransforms && transforms && (
                        <GroupFields
                            object={object}
                            groupStyles={objectTransforms}
                            properties={transforms}
                            handleSetGroupValue={handleSetGroup}
                            handleDeleteGroup={handleDeleteGroup}
                        />
                    )}
                </Box>
            }
        />
    )
}

export default Transforms
